import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * CrashClawKnowledgePanel — 配置页「知识库」同级 Tab
 *
 * TAPD: 1020421727136622376
 *   - 当前开放：iWiki + 本地上传（Knot 暂隐藏，等开放 list API / 业务接入后再开）
 *   - iWiki：关联弹窗（MCP getMySpaces 列表 + 多选）；未授权引导至连接器 Tab
 *   - Knot：无列表接口，填知识库 ID/详情页 URL → resolve 校验后关联（ENABLE_KNOT_KNOWLEDGE）
 *   - 本地：名称 + 描述 + zip；Hover 删除
 *   - 视觉对齐 Skills 卡片气质（圆角、浅阴影、分区标题）
 *
 * API：
 *   POST /redir/api/crashclaw/listKnowledgeBases
 *   POST /redir/api/crashclaw/listIwikiKnowledgeBases
 *   POST /redir/api/crashclaw/listKnotKnowledgeBases
 *   POST /redir/api/crashclaw/resolveKnotKnowledgeBase
 *   POST /redir/api/crashclaw/associateKnowledgeBase
 *   POST /redir/api/crashclaw/uploadPersonalWiki
 *   POST /redir/api/crashclaw/replacePersonalWiki
 *   POST /redir/api/crashclaw/disassociateKnowledgeBase
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Empty, Form, Input, Modal, Spin, Tag, Tooltip, message } from 'antd';
import { CloseOutlined, ExclamationCircleOutlined, FileZipOutlined, PlusOutlined, SearchOutlined, UploadOutlined, } from '@ant-design/icons';
import RestHelper from 'utils/RestHelper';
import { ze } from 'utils/zhEn';
import { MAX_WIKI_FILE_BYTES, MAX_WIKI_FILE_MB } from './crashClawDefaults';
import iwikiLogo from './assets/iwiki.png';
import knotLogo from './assets/knot.png';
import style from './style.scss';
const API_PREFIX = '/redir/api/crashclaw';
/** Knot 知识库入口开关：等 Knot 开放「列我的知识库」后再打开 */
const ENABLE_KNOT_KNOWLEDGE = false;
function formatTime(value) {
    if (!value)
        return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime()))
        return String(value).slice(0, 10);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
function itemTitle(item) {
    return item.displayName
        || item.externalKbName
        || item.fileName
        || `#${item.id}`;
}
function truncateChars(text, max) {
    if (!text)
        return '';
    const chars = Array.from(text.trim());
    if (chars.length <= max)
        return chars.join('');
    return `${chars.slice(0, max).join('')}…`;
}
const CrashClawKnowledgePanel = ({ onNavigateToConnectors, }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    // iWiki 关联弹窗
    const [iwikiOpen, setIwikiOpen] = useState(false);
    const [iwikiLoading, setIwikiLoading] = useState(false);
    const [iwikiSaving, setIwikiSaving] = useState(false);
    const [iwikiAuthorized, setIwikiAuthorized] = useState(true);
    const [iwikiCandidates, setIwikiCandidates] = useState([]);
    const [iwikiSelected, setIwikiSelected] = useState([]);
    const [iwikiKeyword, setIwikiKeyword] = useState('');
    const [iwikiError, setIwikiError] = useState(null);
    // Knot 关联弹窗（填 ID）
    const [knotOpen, setKnotOpen] = useState(false);
    const [knotAuthLoading, setKnotAuthLoading] = useState(false);
    const [knotAuthorized, setKnotAuthorized] = useState(true);
    const [knotResolving, setKnotResolving] = useState(false);
    const [knotSaving, setKnotSaving] = useState(false);
    const [knotInput, setKnotInput] = useState('');
    const [knotResolved, setKnotResolved] = useState(null);
    const [knotError, setKnotError] = useState(null);
    // 本地上传 / 修改弹窗
    const [localModalVisible, setLocalModalVisible] = useState(false);
    const [localModalMode, setLocalModalMode] = useState('create');
    const [editingItem, setEditingItem] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [savingLocal, setSavingLocal] = useState(false);
    const [form] = Form.useForm();
    const fileInputRef = useRef(null);
    const iwikiItems = useMemo(() => items.filter(i => i.source === 'iwiki'), [items]);
    const knotItems = useMemo(() => items.filter(i => i.source === 'knot'), [items]);
    const localItems = useMemo(() => items.filter(i => i.source === 'local'), [items]);
    const associatedIds = useMemo(() => {
        const map = {
            iwiki: new Set(),
            knot: new Set(),
        };
        for (const item of items) {
            if ((item.source === 'iwiki' || item.source === 'knot') && item.externalKbId) {
                map[item.source].add(item.externalKbId);
            }
        }
        return map;
    }, [items]);
    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const { json } = await RestHelper.mustPost(`${API_PREFIX}/listKnowledgeBases`, {});
            setItems(Array.isArray(json.data) ? json.data : []);
        }
        catch (e) {
            console.error(e);
            message.error(ze('加载知识库失败', 'Failed to load knowledge bases'));
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        fetchList();
    }, [fetchList]);
    // -------------------- iWiki associate --------------------
    function closeIwikiModal() {
        setIwikiOpen(false);
        setIwikiCandidates([]);
        setIwikiSelected([]);
        setIwikiKeyword('');
        setIwikiAuthorized(true);
        setIwikiError(null);
    }
    async function openIwikiModal() {
        setIwikiOpen(true);
        setIwikiSelected([]);
        setIwikiKeyword('');
        setIwikiError(null);
        setIwikiAuthorized(true);
        setIwikiCandidates([]);
        setIwikiLoading(true);
        try {
            const { json } = await RestHelper.mustPost(`${API_PREFIX}/listIwikiKnowledgeBases`, {});
            const data = json.data;
            if (!data || data.authorized === false) {
                setIwikiAuthorized(false);
                setIwikiCandidates([]);
                return;
            }
            setIwikiAuthorized(true);
            setIwikiCandidates(Array.isArray(data.items) ? data.items : []);
        }
        catch (e) {
            console.error(e);
            setIwikiError(ze('知识库列表加载失败，请稍后重试', 'Failed to load knowledge base list. Please retry later.'));
        }
        finally {
            setIwikiLoading(false);
        }
    }
    async function confirmIwikiAssociate() {
        if (iwikiSelected.length === 0)
            return;
        setIwikiSaving(true);
        try {
            const selectedItems = iwikiCandidates
                .filter(c => iwikiSelected.includes(c.kbId))
                .map(c => ({
                externalKbId: c.kbId,
                externalKbName: c.kbName,
            }));
            await RestHelper.mustPost(`${API_PREFIX}/associateKnowledgeBase`, {
                source: 'iwiki',
                items: selectedItems,
            });
            message.success(ze('关联成功', 'Associated successfully'));
            closeIwikiModal();
            await fetchList();
        }
        catch (e) {
            console.error(e);
            const err = e;
            message.error(err?.message || err?.json?.message
                || ze('关联失败', 'Failed to associate'));
        }
        finally {
            setIwikiSaving(false);
        }
    }
    // -------------------- Knot associate (manual ID) --------------------
    function closeKnotModal() {
        setKnotOpen(false);
        setKnotInput('');
        setKnotResolved(null);
        setKnotError(null);
        setKnotAuthorized(true);
    }
    async function openKnotModal() {
        setKnotOpen(true);
        setKnotInput('');
        setKnotResolved(null);
        setKnotError(null);
        setKnotAuthorized(true);
        setKnotAuthLoading(true);
        try {
            const { json } = await RestHelper.mustPost(`${API_PREFIX}/listKnotKnowledgeBases`, {});
            if (!json.data || json.data.authorized === false) {
                setKnotAuthorized(false);
            }
        }
        catch (e) {
            console.error(e);
            setKnotError(ze('无法确认 Knot 授权状态，请稍后重试', 'Failed to check Knot authorization'));
        }
        finally {
            setKnotAuthLoading(false);
        }
    }
    async function resolveKnotKb() {
        const raw = String(knotInput || '').trim();
        if (!raw) {
            message.warning(ze('请输入知识库 ID 或详情页链接', 'Enter a knowledge base ID or detail URL'));
            return;
        }
        setKnotResolving(true);
        setKnotResolved(null);
        setKnotError(null);
        try {
            const { json } = await RestHelper.mustPost(`${API_PREFIX}/resolveKnotKnowledgeBase`, {
                kbId: raw,
            });
            const data = json.data;
            if (!data || data.authorized === false) {
                setKnotAuthorized(false);
                return;
            }
            const item = Array.isArray(data.items) && data.items.length > 0 ? data.items[0] : null;
            if (!item) {
                setKnotError(ze('未找到该知识库或无权限访问', 'Knowledge base not found or no access'));
                return;
            }
            setKnotResolved(item);
        }
        catch (e) {
            console.error(e);
            const err = e;
            setKnotError(err?.message || err?.json?.message
                || ze('校验失败，请检查知识库 ID', 'Validation failed. Check the knowledge base ID.'));
        }
        finally {
            setKnotResolving(false);
        }
    }
    async function confirmKnotAssociate() {
        if (!knotResolved) {
            message.warning(ze('请先校验知识库', 'Please validate the knowledge base first'));
            return;
        }
        if (associatedIds.knot.has(knotResolved.kbId)) {
            message.info(ze('该知识库已关联', 'Already associated'));
            return;
        }
        setKnotSaving(true);
        try {
            await RestHelper.mustPost(`${API_PREFIX}/associateKnowledgeBase`, {
                source: 'knot',
                items: [{
                        externalKbId: knotResolved.kbId,
                        externalKbName: knotResolved.kbName,
                    }],
            });
            message.success(ze('关联成功', 'Associated successfully'));
            closeKnotModal();
            await fetchList();
        }
        catch (e) {
            console.error(e);
            const err = e;
            message.error(err?.message || err?.json?.message
                || ze('关联失败', 'Failed to associate'));
        }
        finally {
            setKnotSaving(false);
        }
    }
    // -------------------- Local upload --------------------
    function openCreateLocalModal() {
        setLocalModalMode('create');
        setEditingItem(null);
        setSelectedFile(null);
        form.resetFields();
        setLocalModalVisible(true);
    }
    function openReplaceLocalModal(item) {
        setLocalModalMode('replace');
        setEditingItem(item);
        setSelectedFile(null);
        form.setFieldsValue({
            displayName: item.displayName || '',
            description: item.description || '',
        });
        setLocalModalVisible(true);
    }
    function closeLocalModal() {
        setLocalModalVisible(false);
        setEditingItem(null);
        setSelectedFile(null);
        form.resetFields();
        if (fileInputRef.current)
            fileInputRef.current.value = '';
    }
    function validateZipFile(file) {
        if (!file.name.toLowerCase().endsWith('.zip')) {
            message.error(ze('请上传 .zip 压缩包', 'Please upload a .zip archive'));
            return false;
        }
        if (file.size > MAX_WIKI_FILE_BYTES) {
            message.error(ze(`单个知识库 zip 不能超过 ${MAX_WIKI_FILE_MB}MB`, `Each knowledge base zip must be ${MAX_WIKI_FILE_MB}MB or smaller`));
            return false;
        }
        return true;
    }
    async function handleLocalSubmit() {
        try {
            const values = await form.validateFields();
            if (!selectedFile) {
                message.warning(ze('请选择知识库 zip', 'Please select a knowledge base zip'));
                return;
            }
            if (!validateZipFile(selectedFile))
                return;
            setSavingLocal(true);
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('displayName', String(values.displayName || '').trim());
            formData.append('description', String(values.description || '').trim());
            if (localModalMode === 'create') {
                await RestHelper.mustPost(`${API_PREFIX}/uploadPersonalWiki`, formData);
                message.success(ze('知识库已创建，将同步到 Bridge', 'Knowledge base created; syncing to Bridge'));
            }
            else {
                if (!editingItem)
                    return;
                formData.append('id', String(editingItem.id));
                await RestHelper.mustPost(`${API_PREFIX}/replacePersonalWiki`, formData);
                message.success(ze('知识库已重新上传', 'Knowledge base re-uploaded'));
            }
            closeLocalModal();
            await fetchList();
        }
        catch (e) {
            if (e && typeof e === 'object' && 'errorFields' in e)
                return;
            console.error(e);
            const err = e;
            const detail = err?.message || err?.json?.message;
            message.error(detail
                ? ze(`操作失败：${detail}`, `Operation failed: ${detail}`)
                : ze('操作失败', 'Operation failed'));
        }
        finally {
            setSavingLocal(false);
        }
    }
    // -------------------- Disassociate / delete --------------------
    function handleRemove(item) {
        const label = itemTitle(item);
        const isLocal = item.source === 'local';
        Modal.confirm({
            title: isLocal
                ? ze('删除本地知识库', 'Delete Local Knowledge Base')
                : ze('解除关联', 'Disassociate'),
            icon: _jsx(ExclamationCircleOutlined, {}),
            content: isLocal
                ? ze(`确定删除「${label}」？将清理后端 zip 数据，Agent 将不再能引用其中的文档。`, `Delete "${label}"? Backend zip data will be cleaned; Agents will no longer reference these documents.`)
                : ze(`确认解除关联「${label}」？解除后 Agent 检索将不再包含该知识库，${item.source === 'iwiki' ? 'iWiki' : 'Knot'} 侧原知识库不受影响。`, `Disassociate "${label}"? Agents will stop using it; the original ${item.source === 'iwiki' ? 'iWiki' : 'Knot'} knowledge base is unaffected.`),
            okText: isLocal ? ze('删除', 'Delete') : ze('确认解除', 'Confirm'),
            okType: 'danger',
            cancelText: ze('取消', 'Cancel'),
            onOk: async () => {
                try {
                    await RestHelper.mustPost(`${API_PREFIX}/disassociateKnowledgeBase`, { id: item.id });
                    message.success(isLocal
                        ? ze('知识库已删除', 'Knowledge base deleted')
                        : ze('已解除关联', 'Disassociated'));
                    await fetchList();
                }
                catch (e) {
                    console.error(e);
                    message.error(ze('操作失败', 'Operation failed'));
                }
            },
        });
    }
    // -------------------- Render helpers --------------------
    function renderKbRow(item, source) {
        const title = itemTitle(item);
        const desc = truncateChars(item.description, 40);
        const meta = source === 'local'
            ? [
                item.fileName,
                item.mdFileCount != null ? `${item.mdFileCount} md` : null,
                formatTime(item.createdAt),
            ].filter(Boolean).join(' · ')
            : [
                source === 'knot' ? item.externalKbId : null,
                desc || null,
            ].filter(Boolean).join(' · ');
        return (_jsxs("div", { className: style.kbRow, children: [_jsxs("div", { className: style.kbRowMain, children: [_jsxs("span", { className: style.kbRowIcon, children: [source === 'iwiki' && _jsx("img", { src: iwikiLogo, alt: "iWiki" }), source === 'knot' && _jsx("img", { src: knotLogo, alt: "Knot" }), source === 'local' && _jsx(FileZipOutlined, {})] }), _jsxs("div", { className: style.kbRowText, children: [_jsx("div", { className: style.kbRowName, title: title, children: title }), meta && _jsx("div", { className: style.kbRowMeta, title: item.description || meta, children: meta })] })] }), _jsxs("div", { className: style.kbRowActions, children: [source === 'local' && (_jsx(Tooltip, { title: ze('重新上传', 'Re-upload'), children: _jsx("button", { type: "button", className: style.kbRowActionBtn, onClick: () => openReplaceLocalModal(item), children: _jsx(UploadOutlined, {}) }) })), _jsx(Tooltip, { title: source === 'local' ? ze('删除', 'Delete') : ze('解除关联', 'Disassociate'), children: _jsx("button", { type: "button", className: `${style.kbRowActionBtn} ${style.kbRowDeleteBtn}`, onClick: () => handleRemove(item), children: _jsx(CloseOutlined, {}) }) })] })] }, item.id));
    }
    function renderSourceGroup(opts) {
        return (_jsxs("div", { className: style.kbGroupCard, children: [_jsxs("div", { className: style.kbGroupHeader, children: [_jsxs("div", { className: style.kbGroupTitle, children: [opts.logo ? (_jsx("span", { className: style.kbGroupLogo, children: _jsx("img", { src: opts.logo, alt: "" }) })) : (_jsx("span", { className: style.kbGroupLogo, children: _jsx(FileZipOutlined, {}) })), _jsx("span", { children: opts.title })] }), _jsx(Button, { type: "primary", ghost: true, size: "small", icon: opts.source === 'local' ? _jsx(UploadOutlined, {}) : _jsx(PlusOutlined, {}), onClick: opts.onAction, disabled: loading || (opts.source === 'local' && localItems.length >= 20), children: opts.actionLabel })] }), _jsx("div", { className: style.kbGroupBody, children: opts.list.length === 0 ? (_jsx("div", { className: style.kbGroupEmpty, children: opts.emptyText })) : (opts.list.map(item => renderKbRow(item, opts.source))) })] }));
    }
    const filteredIwikiCandidates = useMemo(() => {
        const kw = iwikiKeyword.trim().toLowerCase();
        if (!kw)
            return iwikiCandidates;
        return iwikiCandidates.filter(c => (c.kbName || '').toLowerCase().includes(kw)
            || (c.description || '').toLowerCase().includes(kw)
            || (c.kbId || '').toLowerCase().includes(kw));
    }, [iwikiCandidates, iwikiKeyword]);
    function renderUnauthorized(label, onClose) {
        return (_jsxs("div", { className: style.kbUnauthorized, children: [_jsx(ExclamationCircleOutlined, { className: style.kbUnauthorizedIcon }), _jsx("div", { className: style.kbUnauthorizedTitle, children: ze(`未授权 ${label}`, `${label} not authorized`) }), _jsx("div", { className: style.kbUnauthorizedDesc, children: ze(`请先到「连接器」Tab 完成 ${label} 授权后再关联知识库。`, `Please authorize ${label} in the Connectors tab first.`) }), _jsx(Button, { type: "primary", onClick: () => {
                        onClose();
                        onNavigateToConnectors?.();
                    }, children: ze('前往连接器 Tab 完成授权', 'Go to Connectors') })] }));
    }
    return (_jsxs("div", { className: style.sectionBody, children: [_jsx("div", { className: style.kbTabIntro, children: ze('配置 CrashClaw 智能体的知识库内容。iWiki 仅同步名称等检索元信息，Agent 通过 MCP 按需取数；本地 zip 会下发到 Bridge。配置为个人级，个人所有有权限的工作区均可读取。', 'Configure knowledge bases for CrashClaw. iWiki syncs metadata only; Agents fetch via MCP. Local zips are synced to Bridge. Personal-level: available in all workspaces you can access.') }), loading ? (_jsxs("div", { className: style.skillsEmpty, children: [_jsx(Spin, {}), " ", ze('加载中…', 'Loading…')] })) : (_jsxs("div", { className: style.kbGroups, children: [renderSourceGroup({
                        source: 'iwiki',
                        title: 'iWiki',
                        logo: iwikiLogo,
                        actionLabel: ze('添加', 'Add'),
                        onAction: openIwikiModal,
                        list: iwikiItems,
                        emptyText: ze('暂无已关联知识库', 'No associated knowledge bases yet'),
                    }), ENABLE_KNOT_KNOWLEDGE && renderSourceGroup({
                        source: 'knot',
                        title: 'Knot',
                        logo: knotLogo,
                        actionLabel: ze('添加', 'Add'),
                        onAction: openKnotModal,
                        list: knotItems,
                        emptyText: ze('暂无已关联知识库', 'No associated knowledge bases yet'),
                    }), renderSourceGroup({
                        source: 'local',
                        title: ze('本地上传', 'Local Upload'),
                        actionLabel: ze('选择 zip', 'Select zip'),
                        onAction: openCreateLocalModal,
                        list: localItems,
                        emptyText: ze('暂无本地知识库，点击右上角上传 zip', 'No local knowledge bases yet. Upload a zip to start.'),
                    })] })), _jsx(Modal, { title: ze('关联 iWiki 知识库', 'Associate iWiki Knowledge Bases'), open: iwikiOpen, onCancel: closeIwikiModal, destroyOnClose: true, width: 560, footer: iwikiAuthorized && !iwikiError ? [
                    _jsx(Button, { onClick: closeIwikiModal, children: ze('取消', 'Cancel') }, "cancel"),
                    _jsx(Button, { type: "primary", disabled: iwikiSelected.length === 0 || iwikiLoading, loading: iwikiSaving, onClick: confirmIwikiAssociate, children: ze('确认关联', 'Confirm') }, "ok"),
                ] : [
                    _jsx(Button, { onClick: closeIwikiModal, children: ze('关闭', 'Close') }, "close"),
                ], children: iwikiLoading ? (_jsxs("div", { className: style.kbAssocLoading, children: [_jsx(Spin, {}), " ", ze('加载中…', 'Loading…')] })) : iwikiError ? (_jsxs("div", { className: style.kbUnauthorized, children: [_jsx(ExclamationCircleOutlined, { className: style.kbUnauthorizedIcon }), _jsx("div", { children: iwikiError })] })) : !iwikiAuthorized ? (renderUnauthorized('iWiki', closeIwikiModal)) : (_jsxs("div", { children: [_jsx(Input, { allowClear: true, prefix: _jsx(SearchOutlined, {}), placeholder: ze('搜索知识库名称…', 'Search by name…'), value: iwikiKeyword, onChange: (e) => setIwikiKeyword(e.target.value), style: { marginBottom: 12 } }), _jsx("div", { className: style.kbAssocList, children: filteredIwikiCandidates.length === 0 ? (_jsx(Empty, { image: Empty.PRESENTED_IMAGE_SIMPLE, description: ze('暂无可关联知识库', 'No knowledge bases available') })) : (filteredIwikiCandidates.map((c) => {
                                const already = associatedIds.iwiki.has(c.kbId);
                                const checked = already || iwikiSelected.includes(c.kbId);
                                return (_jsxs("label", { className: `${style.kbAssocItem} ${already ? style.kbAssocItemDisabled : ''}`, children: [_jsx(Checkbox, { checked: checked, disabled: already, onChange: (e) => {
                                                if (already)
                                                    return;
                                                setIwikiSelected((prev) => {
                                                    if (e.target.checked) {
                                                        return prev.includes(c.kbId) ? prev : [...prev, c.kbId];
                                                    }
                                                    return prev.filter(id => id !== c.kbId);
                                                });
                                            } }), _jsxs("span", { className: style.kbAssocItemText, children: [_jsx("span", { className: style.kbAssocItemName, children: c.kbName || c.kbId }), already && (_jsx(Tag, { className: style.sourceTag, children: ze('已关联', 'Associated') })), c.description && (_jsx("span", { className: style.kbAssocItemDesc, children: truncateChars(c.description, 60) }))] })] }, c.kbId));
                            })) })] })) }), _jsx(Modal, { title: ze('关联 Knot 知识库', 'Associate Knot Knowledge Base'), open: ENABLE_KNOT_KNOWLEDGE && knotOpen, onCancel: closeKnotModal, destroyOnClose: true, width: 520, footer: !knotAuthorized ? [
                    _jsx(Button, { onClick: closeKnotModal, children: ze('关闭', 'Close') }, "close"),
                ] : [
                    _jsx(Button, { onClick: closeKnotModal, children: ze('取消', 'Cancel') }, "cancel"),
                    _jsx(Button, { type: "primary", disabled: !knotResolved || knotAuthLoading, loading: knotSaving, onClick: confirmKnotAssociate, children: ze('确认关联', 'Confirm') }, "ok"),
                ], children: knotAuthLoading ? (_jsxs("div", { className: style.kbAssocLoading, children: [_jsx(Spin, {}), " ", ze('加载中…', 'Loading…')] })) : !knotAuthorized ? (renderUnauthorized('Knot', closeKnotModal)) : (_jsxs("div", { children: [_jsx("div", { className: style.kbUnauthorizedDesc, style: { marginBottom: 12, textAlign: 'left' }, children: ze('Knot 不支持拉取「我的知识库」列表。请打开 Knot 知识库详情页，复制知识库 ID（或粘贴详情页链接），校验通过后再关联。', 'Knot cannot list your knowledge bases. Paste the UUID (or detail page URL) from Knot, validate, then associate.') }), _jsxs("div", { style: { display: 'flex', gap: 8, marginBottom: 12 }, children: [_jsx(Input, { allowClear: true, placeholder: ze('知识库 ID 或详情页链接', 'Knowledge base ID or detail URL'), value: knotInput, onChange: (e) => {
                                        setKnotInput(e.target.value);
                                        setKnotResolved(null);
                                        setKnotError(null);
                                    }, onPressEnter: resolveKnotKb }), _jsx(Button, { loading: knotResolving, onClick: resolveKnotKb, children: ze('校验', 'Validate') })] }), knotError && (_jsx("div", { style: { color: '#cf1322', fontSize: 12, marginBottom: 12 }, children: knotError })), knotResolved && (_jsx("div", { className: style.kbAssocItem, style: { cursor: 'default' }, children: _jsxs("span", { className: style.kbAssocItemText, children: [_jsx("span", { className: style.kbAssocItemName, children: knotResolved.kbName || knotResolved.kbId }), associatedIds.knot.has(knotResolved.kbId) && (_jsx(Tag, { className: style.sourceTag, children: ze('已关联', 'Associated') })), _jsxs("span", { className: style.kbAssocItemDesc, children: ["ID: ", knotResolved.kbId] }), knotResolved.description && (_jsx("span", { className: style.kbAssocItemDesc, children: truncateChars(knotResolved.description, 120) }))] }) }))] })) }), _jsx(Modal, { title: localModalMode === 'create'
                    ? ze('创建本地知识库', 'Create Local Knowledge Base')
                    : ze('修改知识库（重新上传）', 'Edit Knowledge Base (Re-upload)'), open: localModalVisible, onCancel: closeLocalModal, onOk: handleLocalSubmit, okText: localModalMode === 'create' ? ze('创建', 'Create') : ze('重新上传', 'Re-upload'), cancelText: ze('取消', 'Cancel'), confirmLoading: savingLocal, destroyOnClose: true, width: 520, children: _jsxs(Form, { form: form, layout: "vertical", requiredMark: true, children: [_jsx(Form.Item, { name: "displayName", label: ze('知识库名称', 'Knowledge Base Name'), rules: [
                                { required: true, message: ze('请输入知识库名称', 'Please enter a name') },
                                { max: 256, message: ze('名称过长', 'Name is too long') },
                            ], children: _jsx(Input, { placeholder: ze('例如：CrashSight 故障手册', 'e.g. CrashSight runbook') }) }), _jsx(Form.Item, { name: "description", label: ze('知识库描述', 'Description'), rules: [
                                { required: true, message: ze('请输入知识库描述', 'Please enter a description') },
                                { max: 512, message: ze('描述过长', 'Description is too long') },
                            ], children: _jsx(Input.TextArea, { rows: 3, placeholder: ze('简要说明用途，会写入 Agent INDEX / profile', 'Brief purpose; written into Agent INDEX / profile') }) }), _jsxs(Form.Item, { label: ze('知识库文件（zip）', 'Knowledge Base File (zip)'), required: true, extra: ze(`仅支持 .zip 压缩包，单个最大 ${MAX_WIKI_FILE_MB}MB`, `.zip archives only; max ${MAX_WIKI_FILE_MB}MB each`), children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 8 }, children: [_jsx(Button, { icon: _jsx(UploadOutlined, {}), onClick: () => fileInputRef.current?.click(), children: ze('选择 zip', 'Select zip') }), _jsx("span", { style: { fontSize: 12, color: '#86909c', overflow: 'hidden', textOverflow: 'ellipsis' }, children: selectedFile
                                                ? selectedFile.name
                                                : (localModalMode === 'replace'
                                                    ? ze('请选择新的 zip 以覆盖', 'Select a new zip to replace')
                                                    : ze('未选择文件', 'No file selected')) })] }), _jsx("input", { ref: fileInputRef, type: "file", accept: ".zip,application/zip", style: { display: 'none' }, onChange: (e) => {
                                        const file = e.target.files?.[0];
                                        if (file && validateZipFile(file)) {
                                            setSelectedFile(file);
                                        }
                                        e.target.value = '';
                                    } })] })] }) })] }));
};
export default CrashClawKnowledgePanel;
